import {ChangeDetectionStrategy, Component, computed, Inject, Injectable, signal} from "@angular/core";
import {DokumentId, DokumentsigneringRessursClient, DokumentVersjonModell, OtpSigneringValideringResponsModell, Reautentiseringsinformasjon, SendSmsOtpSigneringSpesifikasjon, SigneringOtpSendtModell, TofaktorSigneringskodeKanal} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {OtpSkjemaKomponent} from "web-ng2/delt/komponenter/skjema/otp-felt/otp-felt";
import {MatIcon} from "@angular/material/icon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatToolbar} from "@angular/material/toolbar";
import {TranslateModule} from "@ngx-translate/core";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {i18n, I18nTekst, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {BrukerkontekstAdministrasjon} from "felles-ng2/tjenester/BrukerkontekstAdministrasjon";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {DokumentSigneringDatatjeneste} from "felles-ng2/tjenester/datalager/DokumentSigneringDatatjeneste";
import {lesDurationSerialisert, lesDurationSerialisertOpt} from "felles-ng2/støttefunksjoner/temporal";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {filter, map, merge, Observable, of, switchMap, timer} from "rxjs";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {ReautentiseringDialogKomponent} from "web-ng2/moduler/autentisering/modale/reautentisering-dialog/reautentisering-dialog";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {Temporal} from "temporal-polyfill";
import {caseForEnumMangler} from "felles-ng2/støttefunksjoner/enumer";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MobilnummerFeltKomponent} from "web-ng2/delt/komponenter/mobilnummer-felt/mobilnummer-felt";
import {MatTooltip} from "@angular/material/tooltip";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {ajs_ModelloppdateringTjeneste_DI_TOKEN, AjsModelloppdateringTjeneste} from "felles-ng2/tjenester/ajs/AjsModelloppdateringTjeneste";
import Duration = Temporal.Duration;

const debug = initDebugger('SigneringOtpDialogKomponent');

/**
 * Dialog for signering med otp sent per epost eller sms
 */
@Component({
    selector: 'signering-otp-epost-dialog',
    templateUrl: 'signering-otp-dialog.html',
    styleUrl: 'signering-otp-dialog.scss',
    imports: [
        ReactiveFormsModule,
        OtpSkjemaKomponent,
        MatIcon,
        MatIconButton,
        MatToolbar,
        TranslateModule,
        MatDialogContent,
        MatDialogActions,
        MatButton,
        VisSpinnerDirektiv,
        MatProgressSpinner,
        MatFormField,
        MatLabel,
        MobilnummerFeltKomponent,
        MatTooltip
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SigneringOtpDialogKomponent {

    static vis(dialog: MatDialog, dokumentversjon: DokumentVersjonModell, kanal: TofaktorSigneringskodeKanal): MatDialogRef<SigneringOtpDialogKomponent, SigneringOtpDialogDialogRespons> {
        return dialog.open(SigneringOtpDialogKomponent, {data: {dokumentversjon, kanal} as DialogParametre, disableClose: true, ariaLabelledBy: 'signering-otp-dialog-tittel', minHeight: 250});
    }

    dokumentversjon: DokumentVersjonModell;
    kanal: TofaktorSigneringskodeKanal;

    otpSkjema = new FormGroup({
        otp: new FormControl('', {nonNullable: true, validators: Validators.required})
    });

    /** Dersom kanal er sms og telefonnummer ikke er satt på bruker */
    telefonnummerSkjema = new FormGroup({
        telefonnummer: new FormControl('', {nonNullable: true, validators: [...FeltValidatorer.mobilnummer, Validators.required]})
    });

    otpSendtRespons = signal<SigneringOtpSendtModell | null>(null);
    otpVarighet = computed(() => lesDurationSerialisertOpt(this.otpSendtRespons()?.varighet));
    tidTilOtpUtloept = toSignal(this.tidTilOtpUtloeptSomObserverbar(), {initialValue: null});
    tidTilOtpUtloeptIMinutter = computed(() => Math.round(this.tidTilOtpUtloept()?.total('minutes') ?? 0));

    /** False frem til 60 sekunder etter utsendelse av otp, hvorpå verdien blir true, benyttes for å hindre at bruker klikker gjentatte ganger på 'sendOtp*/
    sendNyOtpTillat = toSignal(toObservable(this.otpSendtRespons).pipe(switchMap(() => merge(of(false), timer(1000 * 60).pipe(map(() => true))))), {initialValue: false});
    otpValideringFeilet = signal(false);
    modus = signal<'angiTelefonnummer' | 'otpIkkeSendt' | 'oppgikode'>('oppgikode');
    senderOtp = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());
    arbeider = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    kanaler = TofaktorSigneringskodeKanal;

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<SigneringOtpDialogKomponent, SigneringOtpDialogDialogRespons>,
                private avhengigheter: SigneringOtpDialogKomponentAvhengigheter,
                private kontekstAdministrasjon: BrukerkontekstAdministrasjon,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private kontekst: BrukerkontekstTilbyder,
                private oversettelser: Oversettelser,
                private klokke: Klokke,
                private matDialog: MatDialog) {
        this.dokumentversjon = parametre.dokumentversjon;
        this.kanal = parametre.kanal;

        if (this.kanal == TofaktorSigneringskodeKanal.sms && this.mobilnummerIkkeRegistrert())
            this.modus.set('angiTelefonnummer');
        else
            this.sendOtp();
    }

    angiMobilnummerOgSendOtp() {
        ReautentiseringDialogKomponent.vis(
          this.matDialog,
          this.oversettelser.oversett(i18n('Bekreft identiteten din for å lagre mobilnummer')),
          autentiseringsinfo => this.oppdaterMobilnummerOgSendOtp(autentiseringsinfo));
    }

    async sendOtp() {
        await this.senderOtp.utfør(async () => {
            switch (this.kanal) {
                case TofaktorSigneringskodeKanal.sms:
                    this.otpSendtRespons.set(await this.avhengigheter.sendSmsOtp(this.dokumentversjon.dokumentId));
                    break;
                case TofaktorSigneringskodeKanal.epost:
                    this.otpSendtRespons.set(await this.avhengigheter.sendEpostOtp(this.dokumentversjon.dokumentId));
                    break;
                default:
                    caseForEnumMangler(this.kanal);
            }
        });

        if (!this.otpSendtRespons()!.sendt) {
            this.modus.set('otpIkkeSendt');
            return;
        }
        this.modus.set('oppgikode');
    }

    async signer() {
        try {
            const respons = await this.arbeider.utfør(() =>
              this.avhengigheter.signer(this.dokumentversjon, this.otpSkjema.value.otp!, this.kanal));

            this.otpValideringFeilet.set(!respons.validert);
            if (!respons.validert) return;

            //dersom epost/mobilnummer ikke var bekreft fra før vil suksessfylt otp-bruk nå ha markert denne som bekreftet
            if ((this.kanal == TofaktorSigneringskodeKanal.sms && !this.kontekst.autentisertBruker().bruker.mobilnummerErBekreftet) ||
              (this.kanal == TofaktorSigneringskodeKanal.epost && !this.kontekst.autentisertBruker().bruker.epostErBekreftet))
                this.kontekstAdministrasjon.relastBruker();

            this.dialogRef.close(true);

        } catch (e) {
            this.meldingTilBruker.feilmelding(new I18nTekst(i18n('En feil oppstod ved signering med engangskode')));
            this.dialogRef.close(false);
        }
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    //private hjelpemetoder

    private tidTilOtpUtloeptSomObserverbar(): Observable<Duration> {
        return toObservable(this.otpSendtRespons)
          .pipe(
            filter(respons => respons?.sendt ?? false),
            map(respons => lesDurationSerialisert(respons!.varighet!)),
            map(varighet => this.klokke.nåSomZonedDateTime().add(varighet)),
            //publiser gjenværende tid hvert 10. sekund
            switchMap(utløpsklokkeslett => timer(0, 10 * 1000)
              .pipe(map(n => this.klokke.nåSomZonedDateTime().until(utløpsklokkeslett)))));
    }

    private mobilnummerIkkeRegistrert() {
        return erTomEllerNil(this.kontekst.autentisertBruker().bruker.mobilnummer);
    }

    private async oppdaterMobilnummerOgSendOtp(autentiseringsinfo: Reautentiseringsinformasjon): Promise<boolean> {
        const spesifikasjon: SendSmsOtpSigneringSpesifikasjon = {
            mobilnummer: this.telefonnummerSkjema.value.telefonnummer,
            autentiseringsinfo: autentiseringsinfo
        }
        const respons = await this.avhengigheter.sendSmsOtp(this.dokumentversjon.dokumentId, spesifikasjon);
        this.otpSendtRespons.set(respons);
        if (!respons.autentiseringOk)
            return false;

        if (respons.sendt == false) {
            this.modus.set('otpIkkeSendt');
            return true;
        }

        this.modus.set('oppgikode');
        return true;
    }

}


export type SigneringOtpDialogDialogRespons = boolean;

export type DialogParametre = {
    dokumentversjon: DokumentVersjonModell;
    kanal: TofaktorSigneringskodeKanal;
}

@Injectable()
export class SigneringOtpDialogKomponentAvhengigheter {

    constructor(private dokumentSigneringDatatjeneste: DokumentSigneringDatatjeneste,
                private dokumentsigneringRessurs: DokumentsigneringRessursClient,
                @Inject(ajs_ModelloppdateringTjeneste_DI_TOKEN) private modelloppdateringTjeneste: AjsModelloppdateringTjeneste) {
    }

    sendEpostOtp(dokumentId: DokumentId): Promise<SigneringOtpSendtModell> {
        return this.dokumentsigneringRessurs.sendEpostOtp(dokumentId).somPromise()
    }

    sendSmsOtp(dokumentId: DokumentId, spesifikasjon: SendSmsOtpSigneringSpesifikasjon = {}): Promise<SigneringOtpSendtModell> {
        return this.dokumentsigneringRessurs.sendSmsOtp(dokumentId, spesifikasjon).somPromise()
    }

    async signer(dokumentversjon: DokumentVersjonModell, otp: string, kanal: TofaktorSigneringskodeKanal): Promise<OtpSigneringValideringResponsModell> {
        const respons = await this.dokumentSigneringDatatjeneste.signerMedOtp(dokumentversjon, otp, kanal);
        //@todo denne utfases når dokumentDataLager er impl
        if (respons.validert) this.modelloppdateringTjeneste.broadcastLokalSignatarSignert(dokumentversjon.dokumentId);
        return respons;
    }
}
